/**
 * Random events store — Zustand wrapper around the pure scheduler.
 *
 * Why separate from `useGameStore`:
 *   - Game state lives in `@sai-clicker/game-core` and stays platform-agnostic.
 *   - Random sensory effects are a pure UI/feel concern with no economic
 *     impact on the game. They have their own lifecycle, cooldowns, and
 *     accessibility toggles, so they get their own store.
 *
 * Wiring:
 *   - `SparkScreen` (or the tap handler in `Spark.tsx`) calls
 *     `useRandomEventsStore.getState().handleTap(epoch, totalTaps)` after
 *     every tap. The store decides whether to emit, and components observe
 *     `activeEffects` to drive their animations.
 */
import { create } from 'zustand';
import { type ActiveEffect, maybeFireOnTap, pruneExpired } from './scheduler.js';
import { emit } from '../telemetry/emit.js';

export interface RandomEventsStore {
  readonly activeEffects: readonly ActiveEffect[];
  readonly lastFiredById: Readonly<Record<string, number>>;
  readonly lastFiredByClass: Readonly<Record<string, number>>;
  /** Master toggle — if false, no effects ever fire. Honors accessibility. */
  readonly enabled: boolean;

  /** Call after every game tap. */
  handleTap(epoch: number, totalTaps: number): void;
  /** Tick — call from a setInterval to expire ended effects. */
  prune(): void;
  /** Accessibility toggle. */
  setEnabled(enabled: boolean): void;
}

export const useRandomEventsStore = create<RandomEventsStore>((set, get) => ({
  activeEffects: [],
  lastFiredById: {},
  lastFiredByClass: {},
  enabled: true,

  handleTap(epoch, totalTaps) {
    if (!get().enabled) return;
    const now = Date.now();
    const ctx = {
      epoch,
      totalTaps,
      lastFiredById: get().lastFiredById,
      lastFiredByClass: get().lastFiredByClass,
      activeEffects: pruneExpired(get().activeEffects, now),
      now,
    };
    const emitted = maybeFireOnTap(ctx);
    if (!emitted) {
      // Still prune in case any are stale.
      const fresh = pruneExpired(get().activeEffects, now);
      if (fresh.length !== get().activeEffects.length) {
        set({ activeEffects: fresh });
      }
      return;
    }
    set({
      activeEffects: [...ctx.activeEffects, emitted],
      lastFiredById: { ...get().lastFiredById, [emitted.id]: now },
      lastFiredByClass: { ...get().lastFiredByClass, [emitted.class]: now },
    });
    // Marker for analyzing which effects players actually see.
    emit('sensory.effect.fired', {
      effect_id: emitted.id,
      class: emitted.class,
      epoch,
    });
  },

  prune() {
    const now = Date.now();
    const fresh = pruneExpired(get().activeEffects, now);
    if (fresh.length !== get().activeEffects.length) {
      set({ activeEffects: fresh });
    }
  },

  setEnabled(enabled) {
    set({ enabled });
  },
}));
